import { Text } from '@lmc/ui-kit/src/general'
import { Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import type { MedicationTableData } from '../types'
import MissedMedicationPopover from './popovers/MissedMedicationPopover'
import OutcomePopover from './popovers/OutcomePopover'
import StatusTooltip from './StatusTooltip'
import { generateDatesArray, MEDICATION_ACTIONS } from './utils'

type MedicationsTableProps = {
  startDate: Dayjs
  endDate: Dayjs
  type: 'scheduled' | 'prn'
  residentFirstName?: string
  onCellClick?: (record: any, dateKey: string) => void
  onMedicationNameClick?: (medicationId: string) => void
  data?: any[]
  onMissedBackSign?: (record: any, dateKey: string) => void
}

const hasPopover = (action: string): boolean => {
  return [
    'ADMINISTERED',
    'REFUSED',
    'PAUSED',
    'NOT_SCHEDULED_OR_NOT_NEEDED',
    'VERBAL_PROMPT',
    'ABSENT',
    'OTHER',
    'ALREADY_TAKEN',
    'LEFT_OUT',
    'NAUSEA_OR_VOMITING',
    'REFUSED_AND_DESTROYED',
    'PRN_AVAILABLE'
  ].includes(action)
}

const canOpenDrawer = (
  outcome: any,
  translationKey: string,
  onCellClick?: (record: any, dateKey: string) => void
): boolean => {
  return !!(
    onCellClick &&
    outcome.administration &&
    translationKey !== 'MISSED' &&
    translationKey !== 'PAUSED'
  )
}

const MedicationsTable = ({
  startDate,
  endDate,
  type,
  residentFirstName = '',
  onCellClick,
  onMedicationNameClick,
  data,
  onMissedBackSign
}: MedicationsTableProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const { id: residentId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const showHourColumn = type === 'scheduled'

  const handleMedicationNameClick = (medicationId: string) => {
    if (onMedicationNameClick) {
      onMedicationNameClick(medicationId)
    } else if (residentId) {
      navigate(`/residents/${residentId}/emar/${medicationId}`)
    }
  }

  const datesArray = generateDatesArray(
    startDate.startOf('day'),
    endDate.endOf('day')
  )

  const medicationRowSpans = React.useMemo(() => {
    if (!data || data.length === 0) {
      return {
        rowSpans: new Map<string, number>(),
        firstRowKeys: new Set<string>(),
        medicationIndexMap: new Map<string, number>()
      }
    }
    const rowSpans = new Map<string, number>()
    const firstRowKeys = new Set<string>()
    const medicationIndexMap = new Map<string, number>()
    const medicationGroups: Record<
      string,
      { count: number; firstKey: string }
    > = {}

    data.forEach((row) => {
      const medicationId = row.residentMedicationId
      if (!medicationGroups[medicationId]) {
        medicationGroups[medicationId] = { count: 0, firstKey: row.key }
        firstRowKeys.add(row.key)
      }
      medicationGroups[medicationId].count += 1
    })

    let medicationIndex = 0
    Object.entries(medicationGroups).forEach(
      ([medicationId, { count, firstKey }]) => {
        rowSpans.set(medicationId, count)
        medicationIndexMap.set(firstKey, medicationIndex)
        medicationIndex++
      }
    )

    return { rowSpans, firstRowKeys, medicationIndexMap }
  }, [data])

  const generateDateColumns = (): ColumnsType<any> => {
    return datesArray.map(({ month, days }) => ({
      title: (
        <YearContainer>
          <SecondaryCellText level={2}>
            {dayjs(month, 'MM/YY').format('MMMM YYYY')}
          </SecondaryCellText>
        </YearContainer>
      ),
      children: days.map((date) => {
        const dateKey = dayjs(date, 'DD/MM/YYYY').format('DD-MM-YYYY')
        return {
          dataIndex: dateKey,
          key: dateKey,
          title: (
            <CommonCell>
              <CellText>{dayjs(date, 'DD/MM/YYYY').format('D')}</CellText>
              <SecondaryCellText level={2}>
                {dayjs(date, 'DD/MM/YYYY').format('ddd')}
              </SecondaryCellText>
            </CommonCell>
          ),
          align: 'center' as const,
          onCell: (record: any) => {
            const cellData = record[dateKey]
            if (
              !cellData ||
              !cellData.outcomes ||
              cellData.outcomes.length === 0
            ) {
              return {
                style: {
                  backgroundColor: theme.color.neutral.color9,
                  padding: 0
                }
              }
            }

            const outcomes = cellData.outcomes
            const lastOutcome = outcomes[outcomes.length - 1]

            if (lastOutcome.action === 'SCHEDULED') {
              return {
                style: {
                  backgroundColor: 'white',
                  padding: 0
                }
              }
            }

            const action = MEDICATION_ACTIONS.find(
              (a) => a.translationKey === lastOutcome.action
            )

            if (!action) {
              return { style: { backgroundColor: 'white', padding: 0 } }
            }

            let backgroundColor = 'white'
            const translationKey = action.translationKey

            if (translationKey === 'ADMINISTERED') {
              backgroundColor = type === 'prn' ? 'white' : '#37A5980D'
            } else if (translationKey === 'NOT_SCHEDULED_OR_NOT_NEEDED') {
              backgroundColor = theme.color.neutral.color9
            } else if (translationKey === 'DISCONTINUED') {
              backgroundColor = theme.color.neutral.color9
            } else if (translationKey === 'PRN_AVAILABLE') {
              backgroundColor = theme.color.neutral.color9
            } else if (translationKey === 'MISSED') {
              backgroundColor = '#E01E4E0D'
            } else if (translationKey === 'PAUSED') {
              backgroundColor = 'white'
            } else {
              backgroundColor = 'white'
            }

            return {
              style: {
                backgroundColor,
                padding: 0
              }
            }
          },
          render: (cellData: any, record: any, index: number) => {
            if (
              !cellData ||
              !cellData.outcomes ||
              cellData.outcomes.length === 0
            ) {
              return null
            }

            const outcomes = cellData.outcomes
            const firstOutcome = outcomes[0]

            if (firstOutcome.action === 'SCHEDULED') {
              if (type === 'prn') {
                return null
              }
              return (
                <StatusTooltip
                  label={`${t('SCHEDULED')} ${t('MEDICATION').toLowerCase()}`}
                >
                  <DateCellContainer />
                </StatusTooltip>
              )
            }

            const action = MEDICATION_ACTIONS.find(
              (a) => a.translationKey === firstOutcome.action
            )

            if (!action) {
              return null
            }

            const translationKey = action.translationKey

            if (outcomes.length > 1) {
              return (
                <DateCellContainer>
                  <IconsContainer>
                    {outcomes.map((outcome: any, index: number) => {
                      const outcomeAction = MEDICATION_ACTIONS.find(
                        (a) => a.translationKey === outcome.action
                      )
                      if (!outcomeAction) return null

                      const handleOutcomeClick = (e: React.MouseEvent) => {
                        if (
                          canOpenDrawer(
                            outcome,
                            outcomeAction.translationKey,
                            onCellClick
                          )
                        ) {
                          e.stopPropagation()
                          const cellData = record[dateKey]
                          if (cellData?.outcomes) {
                            const originalOutcomes = cellData.outcomes
                            cellData.outcomes = [outcome]
                            onCellClick && onCellClick(record, dateKey)
                            cellData.outcomes = originalOutcomes
                          }
                        }
                      }

                      const outcomeContent = outcomeAction.icon ? (
                        <OutcomeIcon
                          $isMissed={outcomeAction.translationKey === 'MISSED'}
                          $isPaused={outcomeAction.translationKey === 'PAUSED'}
                        >
                          {outcomeAction.icon}
                        </OutcomeIcon>
                      ) : outcomeAction.code ? (
                        <OutcomeCode
                          $isMissed={outcomeAction.translationKey === 'MISSED'}
                          $isPaused={outcomeAction.translationKey === 'PAUSED'}
                        >
                          {outcomeAction.code}
                        </OutcomeCode>
                      ) : null

                      const outcomeHasPopover = hasPopover(outcome.action)

                      if (outcomeHasPopover) {
                        return (
                          <OutcomePopover
                            key={index}
                            outcome={outcome}
                            record={record}
                            type={type}
                            residentFirstName={residentFirstName}
                          >
                            <ClickableCell onClick={handleOutcomeClick}>
                              {outcomeContent}
                            </ClickableCell>
                          </OutcomePopover>
                        )
                      }

                      if (
                        outcomeAction.translationKey === 'MISSED' &&
                        !!onMissedBackSign &&
                        !record.medication?.secondSignatureRequired
                      ) {
                        return (
                          <MissedMedicationPopover
                            key={index}
                            onSignForMissed={() => {
                              const cellData = record[dateKey]
                              if (cellData?.outcomes) {
                                const originalOutcomes = cellData.outcomes
                                cellData.outcomes = [outcome]
                                onMissedBackSign(record, dateKey)
                                cellData.outcomes = originalOutcomes
                              }
                            }}
                          >
                            <ClickableCell>{outcomeContent}</ClickableCell>
                          </MissedMedicationPopover>
                        )
                      }

                      if (
                        canOpenDrawer(
                          outcome,
                          outcomeAction.translationKey,
                          onCellClick
                        )
                      ) {
                        return (
                          <ClickableCell
                            key={index}
                            onClick={handleOutcomeClick}
                          >
                            {outcomeContent}
                          </ClickableCell>
                        )
                      }

                      return <div key={index}>{outcomeContent}</div>
                    })}
                  </IconsContainer>
                </DateCellContainer>
              )
            }

            const medicationIndex =
              medicationRowSpans.medicationIndexMap.get(record.key) ?? index
            const cellContent = (
              <DateCellContainer
                data-testid={`emar-date-cell-${dateKey}-${medicationIndex}`}
              >
                {action.icon ? (
                  <OutcomeIcon
                    $isMissed={translationKey === 'MISSED'}
                    $isPaused={translationKey === 'PAUSED'}
                    data-testid={`emar-outcome-icon-${dateKey}-${medicationIndex}`}
                  >
                    {action.icon}
                  </OutcomeIcon>
                ) : action.code ? (
                  <OutcomeCode
                    $isMissed={translationKey === 'MISSED'}
                    $isPaused={translationKey === 'PAUSED'}
                    data-testid={`emar-outcome-code-${dateKey}-${medicationIndex}`}
                  >
                    {action.code}
                  </OutcomeCode>
                ) : null}
              </DateCellContainer>
            )

            const outcomeHasPopover = hasPopover(translationKey)
            const outcomeCanOpenDrawer = canOpenDrawer(
              firstOutcome,
              translationKey,
              onCellClick
            )

            const handleClick = (e: React.MouseEvent) => {
              if (outcomeCanOpenDrawer) {
                e.stopPropagation()
                onCellClick && onCellClick(record, dateKey)
              }
            }

            if (
              translationKey === 'MISSED' &&
              !!onMissedBackSign &&
              !record.medication?.secondSignatureRequired
            ) {
              return (
                <MissedMedicationPopover
                  onSignForMissed={() =>
                    onMissedBackSign && onMissedBackSign(record, dateKey)
                  }
                >
                  <DateCellContainer $clickable>
                    {cellContent}
                  </DateCellContainer>
                </MissedMedicationPopover>
              )
            }

            if (outcomeHasPopover) {
              return (
                <OutcomePopover
                  outcome={firstOutcome}
                  record={record}
                  type={type}
                  residentFirstName={residentFirstName}
                >
                  <ClickableCell onClick={handleClick}>
                    {cellContent}
                  </ClickableCell>
                </OutcomePopover>
              )
            }

            if (outcomeCanOpenDrawer) {
              return (
                <ClickableCell onClick={handleClick}>
                  {cellContent}
                </ClickableCell>
              )
            }

            if (translationKey === 'MISSED') {
              return (
                <StatusTooltip
                  label={`${t('MISSED')} ${t('MEDICATION').toLowerCase()}`}
                >
                  {cellContent}
                </StatusTooltip>
              )
            }

            if (translationKey === 'DISCONTINUED') {
              return (
                <StatusTooltip
                  label={`${t('DISCONTINUED')} ${t('MEDICATION').toLowerCase()}`}
                >
                  {cellContent}
                </StatusTooltip>
              )
            }

            return cellContent
          }
        }
      })
    }))
  }

  const baseColumns: ColumnsType<any> = [
    {
      title: ' ',
      children: [
        {
          title: (
            <MedicationHeaderCell>
              <CellText>{t('MEDICATION')}</CellText>
            </MedicationHeaderCell>
          ),
          dataIndex: 'medication',
          key: 'medication',
          fixed: 'left' as const,
          align: 'left' as const,
          render: (medication: MedicationTableData, record: any) => {
            const medicationId = record.residentMedicationId
            const rowSpan = medicationRowSpans.rowSpans.get(medicationId)
            const isFirstRow = medicationRowSpans.firstRowKeys.has(record.key)

            // Get medication index from the map
            const medicationIndex = isFirstRow
              ? (medicationRowSpans.medicationIndexMap.get(record.key) ?? -1)
              : -1

            const cellContent = isFirstRow ? (
              <MedicationCell
                data-testid={`emar-medication-cell-${medicationIndex}`}
              >
                {onMedicationNameClick || residentId ? (
                  <ClickableMedicationContainer
                    onClick={() => handleMedicationNameClick(medicationId)}
                    data-testid={`emar-medication-name-${medicationIndex}`}
                  >
                    <ClickableMedicationText level={1.5}>
                      {medication.name}
                    </ClickableMedicationText>
                  </ClickableMedicationContainer>
                ) : (
                  <MedicationText
                    level={1.5}
                    data-testid={`emar-medication-name-${medicationIndex}`}
                  >
                    {medication.name}
                  </MedicationText>
                )}
                <LabelValueContainer
                  data-testid={`emar-medication-stock-${medicationIndex}`}
                >
                  <MedicationFieldLabel level={2}>
                    {t('CURRENT_STOCK')}:
                  </MedicationFieldLabel>
                  <MedicationFieldValue
                    level={2}
                    $lowStock={
                      medication.stockAlertLevel !== undefined &&
                      medication.currentStock > 0 &&
                      medication.currentStock <= medication.stockAlertLevel
                    }
                    $outOfStock={medication.currentStock === 0}
                    data-testid={`emar-medication-stock-value-${medicationIndex}`}
                  >
                    {medication.currentStock} {medication.stockUnit}
                  </MedicationFieldValue>
                </LabelValueContainer>
                <LabelValueContainer
                  data-testid={`emar-medication-start-date-${medicationIndex}`}
                >
                  <MedicationFieldLabel level={2}>
                    {t('START_DATE')}:
                  </MedicationFieldLabel>
                  <MedicationFieldValue level={2}>
                    {medication.startDate}
                  </MedicationFieldValue>
                </LabelValueContainer>
                <LabelValueContainer
                  data-testid={`emar-medication-end-date-${medicationIndex}`}
                >
                  <MedicationFieldLabel level={2}>
                    {t('END_DATE')}:
                  </MedicationFieldLabel>
                  <MedicationFieldValue level={2}>
                    {medication.endDate || t('NOT_SET')}
                  </MedicationFieldValue>
                </LabelValueContainer>
                {medication.isControlledDrug && (
                  <Tag color="red">{t('CD')}</Tag>
                )}
                {medication.isDiscontinued && (
                  <Tag color="default">{t('DISCONTINUED')}</Tag>
                )}
              </MedicationCell>
            ) : null

            return {
              children: cellContent,
              props: {
                rowSpan: isFirstRow && rowSpan ? rowSpan : 0
              }
            }
          }
        },
        ...(showHourColumn
          ? [
              {
                title: (
                  <CenteredHeaderCell>
                    <CommonCell>
                      <CellText>{t('HOUR')}</CellText>
                    </CommonCell>
                  </CenteredHeaderCell>
                ),
                dataIndex: 'hour',
                key: 'hour',
                fixed: 'left' as const,
                align: 'center' as const,
                render: (text: string, record: any, rowIndex: number) => {
                  const medicationIndex =
                    medicationRowSpans.medicationIndexMap.get(record.key) ??
                    rowIndex
                  return (
                    <HourCellContent>
                      <CellText
                        data-testid={`emar-medication-hour-${medicationIndex}`}
                      >
                        {text}
                      </CellText>
                    </HourCellContent>
                  )
                }
              }
            ]
          : []),
        {
          title: (
            <DoseCell>
              <CellText>{t('DOSE')}</CellText>
            </DoseCell>
          ),
          dataIndex: 'dose',
          key: 'dose',
          fixed: 'left' as const,
          align: 'center' as const,
          render: (text: string, record: any, rowIndex: number) => {
            const medicationIndex =
              medicationRowSpans.medicationIndexMap.get(record.key) ?? rowIndex
            const match = text?.match(/^([\d.]+)\s+(.+)$/)
            const doseNumber = match ? match[1] : text
            const doseUnit = match?.[2] || null

            const altMatch = record.altDose?.match(/^([\d.]+)\s+(.+)$/)
            const altDoseNumber = altMatch ? altMatch[1] : record.altDose

            return (
              <DoseCellContent
                data-testid={`emar-medication-dose-${medicationIndex}`}
              >
                <CellText>
                  {altDoseNumber
                    ? `${doseNumber} or ${altDoseNumber}`
                    : doseNumber}
                </CellText>
                {doseUnit && (
                  <SecondaryCellText $breakLine={true} level={2}>
                    {doseUnit}
                  </SecondaryCellText>
                )}
              </DoseCellContent>
            )
          }
        }
      ]
    }
  ]

  const columns: ColumnsType<any> = [...baseColumns, ...generateDateColumns()]

  return (
    <StyledTable
      data-testid={`emar-medications-table-${type}`}
      bordered
      columns={columns}
      dataSource={data || []}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  )
}

const YearContainer = styled.div`
  display: flex;
`

const SecondaryCellText = styled(Text)<{ $breakLine?: boolean }>`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
  ${({ $breakLine }) => $breakLine && 'white-space: pre-line;'}
`

const CellText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 500;
  line-height: 1;
`

const MedicationText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const ClickableMedicationText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const ClickableMedicationContainer = styled.div`
  cursor: pointer;
  display: inline-block;

  &:hover ${ClickableMedicationText} {
    text-decoration: underline;
  }
`

const MedicationFieldLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const MedicationFieldValue = styled(Text)<{
  $lowStock?: boolean
  $outOfStock?: boolean
}>`
  color: ${({ theme, $outOfStock, $lowStock }) =>
    $outOfStock
      ? theme.color.status.danger
      : $lowStock
        ? theme.color.status.warning
        : theme.color.neutral.color4};
  font-weight: 500;
`

const LabelValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const MedicationHeaderCell = styled.div`
  width: 180px;
`

const MedicationCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 180px;
  align-items: flex-start;
  padding: 8px;
`

const CenteredHeaderCell = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const CommonCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 32px;
  align-items: center;
`

const DoseCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const DoseCellContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 8px;
`

const HourCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 8px;
`
const DateCellContainer = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  width: 100%;
  height: 100%;
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
`

const IconsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`

const OutcomeIcon = styled.div<{ $isMissed: boolean; $isPaused: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isMissed, $isPaused, theme }) =>
    $isMissed
      ? theme.color.status.danger
      : $isPaused
        ? theme.color.neutral.color5
        : theme.color.neutral.color5};
  svg {
    width: 16px;
    height: 16px;
  }
`

const OutcomeCode = styled(Text)<{ $isMissed: boolean; $isPaused: boolean }>`
  color: ${({ $isMissed, $isPaused, theme }) =>
    $isMissed
      ? theme.color.status.danger
      : $isPaused
        ? theme.color.neutral.color5
        : theme.color.neutral.color3};
  font-weight: 600;
  font-size: 14px;
`

const ClickableCell = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr:first-child > th {
    padding: 8px;
    height: auto;
  }

  .ant-table-thead > tr:nth-child(2) > th {
    padding: 4px 8px;
    height: auto;
  }

  .ant-table-tbody > tr > td {
    padding: 0;
  }
`

export default MedicationsTable
